/**
 * Wallet Tracker Store
 * Zustand store for managing tracked wallets
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { TrackedWallet, WalletFilter } from '../types/wallet.types';

interface WalletTrackerStore {
  // State
  wallets: TrackedWallet[];
  selectedWallet: string | null;
  filter: WalletFilter;

  // Wallet Actions
  addWallet: (wallet: TrackedWallet) => void;
  removeWallet: (address: string) => void;
  updateWallet: (address: string, updates: Partial<TrackedWallet>) => void;
  setSelectedWallet: (address: string | null) => void;

  // Filter Actions
  setFilter: (filter: Partial<WalletFilter>) => void;
  resetFilter: () => void;

  // Utility Actions
  getWallet: (address: string) => TrackedWallet | undefined;
  getFilteredWallets: () => TrackedWallet[];
}

const defaultFilter: WalletFilter = {
  searchQuery: '',
  tags: [],
  chains: [],
  sortBy: 'added',
  sortOrder: 'desc',
};

export const useWalletTrackerStore = create<WalletTrackerStore>()(
  persist(
    (set, get) => ({
      // Initial State
      wallets: [],
      selectedWallet: null,
      filter: defaultFilter,

      // Wallet Actions
      addWallet: (wallet) =>
        set((state) => {
          // Prevent duplicates (case-insensitive)
          const exists = state.wallets.some(
            (w) => w.address.toLowerCase() === wallet.address.toLowerCase()
          );
          if (exists) {
            return state;
          }

          return {
            wallets: [...state.wallets, wallet],
          };
        }),

      removeWallet: (address) =>
        set((state) => ({
          wallets: state.wallets.filter(
            (w) => w.address.toLowerCase() !== address.toLowerCase()
          ),
          selectedWallet:
            state.selectedWallet?.toLowerCase() === address.toLowerCase()
              ? null
              : state.selectedWallet,
        })),

      updateWallet: (address, updates) =>
        set((state) => ({
          wallets: state.wallets.map((w) =>
            w.address.toLowerCase() === address.toLowerCase()
              ? { ...w, ...updates }
              : w
          ),
        })),

      setSelectedWallet: (address) =>
        set({ selectedWallet: address }),

      // Filter Actions
      setFilter: (filterUpdates) =>
        set((state) => ({
          filter: { ...state.filter, ...filterUpdates },
        })),

      resetFilter: () =>
        set({ filter: defaultFilter }),

      // Utility Actions
      getWallet: (address) => {
        const state = get();
        return state.wallets.find(
          (w) => w.address.toLowerCase() === address.toLowerCase()
        );
      },

      getFilteredWallets: () => {
        const state = get();
        let filtered = [...state.wallets];

        // Search filter
        if (state.filter.searchQuery) {
          const query = state.filter.searchQuery.toLowerCase();
          filtered = filtered.filter(
            (w) =>
              w.address.toLowerCase().includes(query) ||
              w.label.toLowerCase().includes(query) ||
              w.ensName?.toLowerCase().includes(query) ||
              w.tags.some((tag) => tag.toLowerCase().includes(query))
          );
        }

        // Tags filter
        if (state.filter.tags.length > 0) {
          filtered = filtered.filter((w) =>
            state.filter.tags.some((tag) => w.tags.includes(tag))
          );
        }

        // Chain filter
        if (state.filter.chains.length > 0) {
          filtered = filtered.filter((w) =>
            state.filter.chains.includes(w.chain)
          );
        }

        // Sorting
        filtered.sort((a, b) => {
          let comparison = 0;

          switch (state.filter.sortBy) {
            case 'name':
              comparison = a.label.localeCompare(b.label);
              break;
            case 'added':
              comparison =
                new Date(a.addedAt).getTime() - new Date(b.addedAt).getTime();
              break;
            case 'value':
              // Value sorting will be implemented when we have balance data
              comparison = 0;
              break;
            case 'activity':
              // Activity sorting will be implemented when we have transaction data
              comparison = 0;
              break;
          }

          return state.filter.sortOrder === 'asc' ? comparison : -comparison;
        });

        return filtered;
      },
    }),
    {
      name: 'edouard_ai_wallet_tracker',
      version: 1,
    }
  )
);
